import { Box, Flex, IconButton, Tooltip } from '@chakra-ui/react';
import { MdEdit, MdOutlineAltRoute } from 'react-icons/md';

import { Employee, Level, StructureNode } from '@app/api/models/structure';
import { Txt } from '@app/components/common';
import { booleanBodyRender, entityBaseBodyRender, TableColumn } from '@app/components/common/table';

import { ParentColumn } from './components/parent-column';

export const useColumns = ({ levels, onEdit, canChange, onResubordinate }: {
  levels: Level[];
  onEdit: (item: StructureNode) => void;
  onResubordinate: (item: StructureNode) => void;
  canChange: boolean;
}): TableColumn<StructureNode>[] => {
  return [
    {
      name: 'id',
      label: '#',
      options: {
        filter: {
          type: 'text',
          name: 'id',
        },
        sort: true,
      },
    },
    {
      name: 'level',
      label: 'Уровень',
      options: {
        filter: {
          type: 'select',
          name: 'level_id',
          onOptionsFetch: () => levels,
        },
        sort: true,
        customBodyRender: entityBaseBodyRender(),
      },
    },
    {
      name: 'boss_id',
      label: 'Руководитель [ID]',
      options: {
        filter: {
          type: 'text',
          name: 'boss_id',
        },
        sort: true,
        customBodyRender: (boss_id) => {
          return <ParentColumn bossId={boss_id} />;
        },
      },
    },
    {
      name: 'territory',
      label: 'Территория',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'position',
      label: 'Должность',
      options: {
        filter: {
          type: 'select',
          name: 'position_id',
          sourceName: 'user_position',

        },
        sort: true,
        customBodyRender: entityBaseBodyRender(),
      },
    },
    {
      name: 'employee',
      label: 'Сотрудник',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (employee: Employee | null, { rowData }) => {          
          if (!employee) {
            return <Txt color='red.500'>Вакансия</Txt>;
          }
          return <Box >
            <Txt color='brand.500'>{employee.name}</Txt>
            <Box display='flex'>
              <Txt color='gray.500'>{rowData.position.name}</Txt>
            </Box>
          </Box>;
        },
      },
    },
    {
      name: 'route_number',
      label: 'Номер маршрута',
      options: {
        filter: {
          type: 'autocomplete-paginated',
          name: 'route_number_id',
          sourceName: 'route_number',
        },
        sort: true,
        customBodyRender: (route_number: EntityBase | null, { rowData }) => {
          if (!route_number) {
            return null;
          }
          return <Box >
            <Txt color='brand.500'>{route_number.name}</Txt> 
          </Box>;
        },
      },
    },
    {
      name: 'is_active',
      label: 'Активность',
      options: {
        filter: {
          type: 'boolean',
          name: 'is_active',
        },
        sort: true,
        customBodyRender: booleanBodyRender(),
      },
    },
    ...(canChange ? [{
      name: 'id',
      label: 'Действия',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (id, { rowData }) => {
          const isTopLevel = levels[0].id === rowData.level.id;
          return (
            <Flex gap={2}>
              {!isTopLevel && <Tooltip label='Переподчинить'>
                <IconButton name="edit" colorScheme='brand' onClick={() => onResubordinate(rowData)} aria-label="edit" size='sm'>
                  <MdOutlineAltRoute />
                </IconButton>
              </Tooltip>}
              <IconButton name="edit" onClick={() => onEdit(rowData)} aria-label="edit" size='sm'>
                <MdEdit />
              </IconButton>
            </Flex>
          );
        },
      },
    }] as TableColumn<StructureNode>[] : []),
  ];
};